import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { BreakpointService } from 'src/app/services/breakpoint/breakpoint.service';
import { CartService } from 'src/app/services/cart/cart.service';
import { DataService } from 'src/app/services/data/data.service';
import { Brand, Category } from 'src/app/shared/interface';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-site-chrome',
  templateUrl: './site-chrome.component.html',
  styleUrls: ['./site-chrome.component.scss'],
  standalone: false,
})
export class SiteChromeComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('siteHeader', { static: false }) siteHeader?: ElementRef<HTMLElement>;

  cartCount = 0;
  user: any = null;
  currentUrl = '';
  isWide = false;
  menuOpen = false;
  environment = environment;

  readonly fallbackFooterCategories: string[] = [];
  readonly fallbackFooterBrands: string[] = [];
  footerCategories: Array<Category | string> = [...this.fallbackFooterCategories];
  footerBrands: Array<Brand | string> = [...this.fallbackFooterBrands];
  siteInfo: any = {};

  private subs: Subscription[] = [];

  constructor(
    private breakpoint: BreakpointService,
    private cartService: CartService,
    private dataService: DataService,
    private authService: AuthService,
    private router: Router,
    private navCtrl: NavController,
    private elRef: ElementRef
  ) {}

  get showChrome(): boolean {
    if (!this.isWide) return false;
    const authPaths = ['/login', '/register', '/otp', '/welcome', '/update-password'];
    return !authPaths.some((path) => this.currentUrl.startsWith(path));
  }

  get socialLinks(): Array<{ label: string; icon: string; url: string; brand: string }> {
    const info = this.siteInfo || {};
    const links = [
      { label: 'Instagram', icon: 'assets/icon/instagram.svg', url: info.instagram || '', brand: 'instagram' },
      { label: 'Facebook', icon: 'assets/icon/facebook.svg', url: info.facebook || '', brand: 'facebook' },
      { label: 'WhatsApp', icon: 'assets/icon/whatsapp.svg', url: this.whatsappUrl, brand: 'whatsapp' },
      { label: 'Snapchat', icon: 'assets/icon/snapchat.svg', url: info.snapchat || '', brand: 'snapchat' },
    ];
    return links.filter((link) => !!link.url);
  }

  get whatsappUrl(): string {
    const raw = this.siteInfo?.whatsapp;
    if (!raw) return '';
    const digits = String(raw).replace(/[^\d+]/g, '');
    if (!digits) return '';
    return `https://api.whatsapp.com/send?phone=${digits.replace(/^\+/, '')}`;
  }

  get googlePlayUrl(): string {
    return this.siteInfo?.googlePlay || this.siteInfo?.playStore || '';
  }

  get appleStoreUrl(): string {
    return this.siteInfo?.appleStore || this.siteInfo?.appStore || '';
  }

  get hasStoreLinks(): boolean {
    return !!(this.googlePlayUrl || this.appleStoreUrl);
  }

  get ordersPath(): string {
    return this.dataService.user?._id ? '/tabs/tab2' : '/login';
  }

  get cartPath(): string {
    return '/cart';
  }

  get navLinks() {
    const url = this.currentUrl || '';
    const links = [
      { label: 'الرئيسية', path: '/tabs/tab1', active: url.includes('/tabs/tab1') },
      {
        label: 'الأقسام',
        path: '/categories',
        active: url.startsWith('/categories') || url.startsWith('/category'),
      },
      {
        label: 'البراندات',
        path: '/brands',
        active: url.startsWith('/brands') || url === '/brand' || url.startsWith('/brand?'),
      },
      {
        label: 'العروض',
        path: '/offers',
        active: url.startsWith('/offers') || url.startsWith('/offer'),
      },
      {
        label: 'المفضلة',
        path: '/tabs/tab4',
        active: url.includes('/tabs/tab4'),
      },
    ];
    if (this.user?._id) {
      links.push({
        label: 'طلباتي',
        path: '/tabs/tab2',
        active: url.includes('/tabs/tab2') || url.startsWith('/order-detail'),
      });
    }
    links.push({ label: 'تواصل معنا', path: '/contact', active: url.startsWith('/contact') });
    return links;
  }

  trackNavLink(_: number, link: { path: string }): string {
    return link.path;
  }

  trackFooterItem(_: number, item: Category | Brand | string): string {
    if (typeof item === 'string') return item;
    return item?._id || item?.name || `${_}`;
  }

  footerItemLabel(item: Category | Brand | string): string {
    return typeof item === 'string' ? item : item?.name || '';
  }

  handleNav(path: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    if (!path || path === this.currentUrl) return;
    this.menuOpen = false;
    void this.navCtrl.navigateRoot(path, { animated: false });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.menuOpen) return;
    const target = event.target as Node | null;
    const header = this.siteHeader?.nativeElement;
    if (header && target && !header.contains(target)) {
      this.menuOpen = false;
    }
  }
  private syncBodyClasses(): void {
    const wide = this.isWide;
    const chrome = this.showChrome;
    document.documentElement.classList.toggle('layout-wide', wide);
    document.documentElement.classList.toggle('site-chrome-active', chrome);
    document.body.classList.toggle('layout-wide', wide);
    document.body.classList.toggle('site-chrome-active', chrome);
  }

  private scrollTop(): void {
    if (!this.isWide) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }

  private mountHeaderOutsideIonic(): void {
    const el = this.siteHeader?.nativeElement;
    if (!el || !this.showChrome) return;

    const isHiddenPage = this.elRef.nativeElement.closest('.ion-page-hidden, .ion-page-invisible');
    if (isHiddenPage) {
      el.style.display = 'none';
      return;
    }

    el.style.display = '';

    const allHeaders = document.querySelectorAll('header.site-header');
    allHeaders.forEach(h => {
      if (h.parentElement === document.body && h !== el) {
        h.remove();
      }
    });

    if (el.parentElement !== document.body) {
      document.body.appendChild(el);
    }
  }

  ngAfterViewChecked(): void {
    this.mountHeaderOutsideIonic();
  }

  private loadFooterData(): void {
    this.subs.push(
      this.dataService.getData<Category[]>('/category?status=1').subscribe({
        next: (categories) => {
          this.footerCategories = categories?.length
            ? categories.slice(0, 5)
            : [...this.fallbackFooterCategories];
        },
        error: () => {
          this.footerCategories = [...this.fallbackFooterCategories];
        },
      }),
      this.dataService.getData<Brand[]>('/brand?status=1').subscribe({
        next: (brands) => {
          this.footerBrands = brands?.length
            ? brands.slice(0, 4)
            : [...this.fallbackFooterBrands];
        },
        error: () => {
          this.footerBrands = [...this.fallbackFooterBrands];
        },
      }),
      this.dataService.getData<any>('/info').subscribe({
        next: (info) => {
          this.siteInfo = info || {};
        },
        error: () => {
          this.siteInfo = {};
        },
      })
    );
  }

  openExternal(url: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!url) return;
    window.open(url, '_blank', 'noopener');
  }

  ngOnInit(): void {
    this.user = this.dataService.user;
    this.currentUrl = this.router.url || '';
    this.loadFooterData();

    this.subs.push(
      this.breakpoint.isWide$.subscribe((wide) => {
        this.isWide = wide;
        if (!wide) this.menuOpen = false;
        this.syncBodyClasses();
      }),
      this.cartService.cartCount.subscribe((count) => {
        this.cartCount = count;
      }),
      this.dataService.user$.subscribe((user) => {
        this.user = user;
      }),
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.currentUrl = event.urlAfterRedirects || event.url;
          this.menuOpen = false;
          this.syncBodyClasses();
          this.scrollTop();
        })
    );
    this.syncBodyClasses();
  }

  async logout(): Promise<void> {
    this.menuOpen = false;
    await this.authService.logOut();
  }

  goHome(): void {
    this.handleNav('/tabs/tab1');
  }
  goToMyOrders(): void {
    this.handleNav(this.ordersPath);
  }
  goToContact(): void {
    this.handleNav('/contact');
  }

  openFooterCategory(item: Category | string): void {
    if (typeof item === 'string' || !item?._id) return;
    this.handleNav(`/category/${item._id}`);
  }

  openFooterBrand(item: Brand | string): void {
    if (typeof item === 'string' || !item?._id) return;
    this.dataService.params = { brand: item };
    this.handleNav('/brand');
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
    this.user = this.dataService.user;
  }

  ngOnDestroy(): void {
    const el = this.siteHeader?.nativeElement;
    if (el?.parentElement === document.body) {
      el.remove();
    }
    this.subs.forEach((sub) => sub.unsubscribe());
    document.documentElement.classList.remove('layout-wide', 'site-chrome-active');
    document.body.classList.remove('layout-wide', 'site-chrome-active');
  }
}